import React, { useState, Suspense, lazy, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import './styles.css';
import AddMemberForm from '../AddMember';
import LoadingSpinner from '../../components/LoadingSpinner';
import SkeletonLoader from '../../components/SkeletonLoader';
import FeatureExplorer from '../../components/FeatureExplorer';
import SimplifiedDashboard from '../../components/SimplifiedDashboard';
import { useDashboardData, useMemberData, useStorageUsage, useRefreshData, useSoftSuccessionContext } from '../../hooks/useApi';

// Lazy load dashboard components for better performance
const UserDashboard = lazy(() => import('./UserDashboard'));
const AdminDashboard = lazy(() => import('./AdminDashboard'));

function getUsageColor(percent) {
  if (percent < 0.7) return '#4CAF50'; // green
  if (percent < 0.9) return '#FFC107'; // yellow
  return '#F44336'; // red
}

export default function Dashboard() {
  const { memberId } = useParams();
  const [searchParams] = useSearchParams();
  const [showAddMember, setShowAddMember] = useState(false);
  const [showFeatureExplorer, setShowFeatureExplorer] = useState(false);
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [passcodeVerified, setPasscodeVerified] = useState(false);
  
  // Use React Query hooks for data fetching
  const { data: currentMember, isLoading: memberLoading, error: memberError } = useMemberData(memberId);
  const { data: dashboardData, isLoading: dashboardLoading, error: dashboardError } = useDashboardData(memberId);
  const { data: usage, isLoading: usageLoading, error: usageError } = useStorageUsage();
  const refreshData = useRefreshData();
  const { data: softSuccessionContext } = useSoftSuccessionContext(memberId);
  const softSuccessionActive = softSuccessionContext?.softSuccessionActive === true;

  // SECURITY CHECK: Verify passcode access for this member
  useEffect(() => {
    const checkPasscodeAccess = () => {
      const sessionMember = sessionStorage.getItem('currentMember');
      const sessionTimestamp = sessionStorage.getItem('passcodeVerifiedAt');
      
      if (sessionMember && sessionTimestamp) {
        const parsedMember = JSON.parse(sessionMember);
        const timestamp = parseInt(sessionTimestamp);
        const now = Date.now();
        
        // Check if session is not older than 30 minutes (1800000 ms)
        const isSessionValid = (now - timestamp) < 1800000;
        
        // Check if the session member matches the URL memberId
        const isMemberMatch = parsedMember._id === memberId || parsedMember.id === memberId;
        
        if (isMemberMatch && isSessionValid) {
          setPasscodeVerified(true);
          return;
        }
      }
      
      // If no valid session, redirect to member selection
      console.warn('🚨 SECURITY: Unauthorized dashboard access attempt for member:', memberId);
      sessionStorage.removeItem('currentMember');
      sessionStorage.removeItem('passcodeVerifiedAt');
      window.location.href = '/member-selection';
    };

    if (memberId) {
      checkPasscodeAccess();
    }
  }, [memberId]);

  // Handle payment success notification
  useEffect(() => {
    const paymentSuccess = searchParams.get('payment_success');
    if (paymentSuccess === 'true') {
      setShowPaymentSuccess(true);
      // Auto-hide after 5 seconds
      setTimeout(() => setShowPaymentSuccess(false), 5000);
      // Clean up URL parameters
      const newUrl = new URL(window.location);
      newUrl.searchParams.delete('payment_success');
      newUrl.searchParams.delete('session_id');
      window.history.replaceState({}, '', newUrl);
    }
  }, [searchParams]);

  // Determine loading and error states
  const loading = memberLoading || dashboardLoading || usageLoading;
  const error = memberError || dashboardError || usageError;

  // SECURITY: Don't render dashboard until passcode is verified
  if (!passcodeVerified) {
    return <LoadingSpinner message="Verifying access..." />;
  }

  if (loading) {
    return (
      <div className="dashboard-bg">
        <div className="dashboard-container">
          <SkeletonLoader type="dashboard" />
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="dashboard-bg">
        <div className="dashboard-container">
          <div className="dashboard-error">
            <h2>Error</h2>
            <p>{error?.message || error || 'An unexpected error occurred'}</p>
            <button onClick={() => window.location.reload()}>Try Again</button>
          </div>
        </div>
      </div>
    );
  }
  if (!currentMember) {
    return (
      <div className="dashboard-bg">
        <div className="dashboard-container">
          <div className="dashboard-error">
            <h2>No Data Available</h2>
            <p>Please try logging in again.</p>
            <button onClick={() => window.location.reload()}>Refresh</button>
          </div>
        </div>
      </div>
    );
  }
  return (
    <>
      {/* Payment Success Notification */}
      {showPaymentSuccess && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          background: 'linear-gradient(135deg, #4CAF50, #45a049)',
          color: 'white',
          padding: '16px 24px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          animation: 'slideInRight 0.3s ease-out'
        }}>
          <div style={{ fontSize: '24px' }}>🎉</div>
          <div>
            <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Payment Successful!</div>
            <div style={{ fontSize: '14px', opacity: 0.9 }}>Your subscription is now active. Welcome to EternaVaults!</div>
          </div>
          <button 
            onClick={() => setShowPaymentSuccess(false)}
            style={{
              background: 'none',
              border: 'none',
              color: 'white',
              fontSize: '18px',
              cursor: 'pointer',
              padding: '4px',
              marginLeft: '8px'
            }}
          >
            ×
          </button>
        </div>
      )}
      
      <Suspense fallback={<SkeletonLoader type="dashboard" />}>
        {process.env.REACT_APP_USE_SIMPLIFIED_DASHBOARD === 'true' ? (
          <SimplifiedDashboard 
            currentMember={currentMember} 
            dashboardData={dashboardData} 
            setShowAddMember={setShowAddMember}
            setShowFeatureExplorer={setShowFeatureExplorer}
          />
        ) : currentMember.isAdmin || softSuccessionActive ? (
          <AdminDashboard 
            currentMember={currentMember} 
            dashboardData={dashboardData} 
            setShowAddMember={setShowAddMember} 
            usage={usage}
            refreshUsage={refreshData}
            setShowFeatureExplorer={setShowFeatureExplorer}
            refreshData={refreshData}
            isSoftSuccessionLocked={softSuccessionActive && !currentMember.isAdmin}
          />
        ) : (
          <UserDashboard 
            currentMember={currentMember} 
            dashboardData={dashboardData} 
            setShowAddMember={setShowAddMember}
            setShowFeatureExplorer={setShowFeatureExplorer}
          />
        )}
      </Suspense>
      {showAddMember && (
        <AddMemberForm
          familyId={currentMember.family ? String(currentMember.family._id || currentMember.family) : undefined}
          usage={usage}
          onClose={() => setShowAddMember(false)}
          onMemberAdded={() => {
            setShowAddMember(false);
            refreshData(); // Refresh usage data after adding member
            window.location.reload();
          }}
        />
      )}
      {showFeatureExplorer && (
        <FeatureExplorer 
          currentMember={currentMember}
          onClose={() => setShowFeatureExplorer(false)}
        />
      )}
    </>
  );
}
